import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
    Button,
    Skeleton,
    Widget,
    WidgetActions,
    WidgetContent,
    WidgetHeader,
    WidgetTitle,
} from "frontend-components";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getListTaskCounts } from "./utils";
import { useListsWithTasks } from "@dashboard/hooks/useListWithTasks";
import { useHseInstance } from "@dashboard/hooks/useHseInstance";
import { Can } from "@dashboard/casl/taskAbility";
import { subject } from "@casl/ability";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faPlus } from "@fortawesome/sharp-regular-svg-icons";
import { useSelectedCustomerIdParam } from "@dashboard/contexts/selectedCustomerContext";

type ListData = {
    id: number;
    title: string;
    done: number;
    total: number;
};

function ProgressBox({ id, title, done, total }: ListData) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const percentage = total > 0 ? (done / total) * 100 : 0;
    const isComplete = percentage >= 100;
    const isLow = percentage < 50;

    return (
        <div className="min-w-0">
            <button
                type="button"
                onClick={() =>
                    void navigate({
                        to: ".",
                        search: (prev) => ({ ...prev, list: id, editList: undefined }),
                    })
                }
                className="text-label text-compendia-foreground block w-full cursor-pointer text-left font-medium hover:underline"
                title={title}
            >
                {title}
            </button>
            <div className="text-body-sm mt-2 flex items-center justify-between">
                <span className="text-compendia-muted">{t("common.progress")}</span>
                <span className={isLow ? "text-chameleon-pink" : "text-compendia-gray-80"}>
                    {done}/{total}
                </span>
            </div>
            <div className="bg-compendia-border mt-1 h-1 w-full rounded-full">
                <div
                    className={`${
                        isComplete ? "bg-chameleon-green" : "bg-chameleon-yellow"
                    } h-1 rounded-full transition-[width] duration-500 ease-out`}
                    style={{ width: `${percentage}%` }}
                ></div>
            </div>
        </div>
    );
}

function ProgressBoxSkeleton() {
    return (
        <div className="min-w-0">
            <Skeleton className="mb-2 h-4 w-2/3 rounded" />
            <div className="mt-2 flex items-center justify-between gap-2">
                <Skeleton className="h-4 w-16 rounded" />
                <Skeleton className="h-4 w-10 rounded" />
            </div>
            <div className="mt-1">
                <Skeleton className="h-1 w-full rounded-full" />
            </div>
        </div>
    );
}

export default function MyLists() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const customerId = useSelectedCustomerIdParam();
    const dashboardProfileParams = useParams({
        from: "/_hub/$customerId/dashboard/$profileId",
    });

    const { instanceId } = useHseInstance();
    const { data, isLoading, isError } = useListsWithTasks({
        count: 6,
        instanceId,
        template: false,
    });

    const listItems = useMemo(() => {
        if (isLoading) {
            return Array(6)
                .fill(null)
                .map((_, index) => <ProgressBoxSkeleton key={`skeleton-${index}`} />);
        }

        return (data ?? []).map((list) => {
            const { completedTasks, totalTasks } = getListTaskCounts(list);
            return (
                <ProgressBox
                    key={list.id}
                    id={list.id}
                    title={list.title}
                    done={completedTasks}
                    total={totalTasks}
                />
            );
        });
    }, [data, isLoading]);

    return (
        <Widget>
            <WidgetHeader>
                <WidgetTitle>
                    <Link
                        to="/$customerId/dashboard/$profileId/lists"
                        params={{
                            customerId,
                            profileId: dashboardProfileParams.profileId,
                        }}
                        className="group inline-flex items-center gap-2 hover:underline"
                    >
                        {t("common.checklists")}
                        <FontAwesomeIcon
                            icon={faArrowRight}
                            className="text-compendia-greige group-hover:text-compendia-foreground size-4 transition-all duration-300 ease-out group-hover:translate-x-1"
                        />
                    </Link>
                </WidgetTitle>
                <WidgetActions>
                    <Can I="create" this={subject("TaskList", { instanceId })}>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t("list.newListTitle")}
                            onClick={() =>
                                void navigate({
                                    to: ".",
                                    search: (prev) => ({
                                        ...prev,
                                        list: "new",
                                        editList: undefined,
                                    }),
                                })
                            }
                        >
                            <FontAwesomeIcon icon={faPlus} />
                        </Button>
                    </Can>
                </WidgetActions>
            </WidgetHeader>
            <WidgetContent className="gap-4 whitespace-pre-wrap break-words hyphens-auto">
                {isError ? (
                    <p className="text-chameleon-pink">{t("common.errorLoadingData")}</p>
                ) : (
                    <div className="grid grid-cols-1 gap-4">{listItems}</div>
                )}
            </WidgetContent>
        </Widget>
    );
}
